/**
 * The service tiles behave as a filter, not as links.
 *
 * In the source design the home page opens with every tile desaturated and
 * nothing selected; picking one saturates it, rings it in brand red, and
 * reveals the section that explains that service. Picking it again collapses
 * back to the neutral state. Powder and cerakote share the process steps;
 * laser engraving reveals the material rail; apparel reveals the branding
 * promo.
 *
 * Sections opt in by carrying `data-nh-reveal` with one or more space- or
 * comma-separated service keys. They live in sibling Shopify sections, which
 * cannot see each other, so the lookup is document-wide.
 */
class NutthouseServices extends HTMLElement {
  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll('[data-nh-service]'));
    this.selected = null;
    this.addEventListener('click', this.#onClick);
    this.#apply(null);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const tile = event.target.closest('[data-nh-service]');
    if (!tile || !this.contains(tile)) return;

    event.preventDefault();
    const key = tile.dataset.nhService;
    this.#apply(this.selected === key ? null : key);
  };

  #apply(key) {
    this.selected = key;

    for (const tile of this.tiles) {
      tile.setAttribute('aria-pressed', String(tile.dataset.nhService === key));
    }

    for (const target of document.querySelectorAll('[data-nh-reveal]')) {
      const keys = target.dataset.nhReveal.split(/[\s,]+/).filter(Boolean);
      target.hidden = !(key && keys.includes(key));
    }

    /* Only scroll for a deliberate selection, and only when the revealed
       section is off screen — deselecting should leave the page still. */
    if (!key) return;
    const revealed = document.querySelector(`[data-nh-reveal~="${CSS.escape(key)}"]:not([hidden])`);
    if (!revealed) return;

    const { top, bottom } = revealed.getBoundingClientRect();
    if (top < window.innerHeight && bottom > 0) return;
    revealed.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('nutthouse-services')) {
  customElements.define('nutthouse-services', NutthouseServices);
}
